import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { type LiteQuote } from '@frontend/api';
import { SHIPPING_METHOD_LABEL } from '@frontend/api';

import { PAYMENT_METHOD, SHIPPING_TYPE } from 'constants/all';

import { toMoment } from 'utils/dateUtils';
import { formatPrice } from 'utils/formatters';

// eslint-disable-next-line no-restricted-imports
import {
  getShippingCost,
  getShippingRate,
  getShippingType,
  getSubtotalCost,
  getTaxCost,
  getTotalCost,
  getHasValidShipping,
} from '../../data/reducer';
import { SHIPPING_TYPE_LABEL } from '../constants';

const StyledTable = styled.table`
  border-collapse: collapse;
  font-size: 13px;
  width: 100%;
`;

const InfoCell = styled.td`
  padding-right: 40px;
`;

const LabelCell = styled.td`
  font-size: 13px;
  font-weight: bold;
  text-align: right;
`;

const ValueCell = styled.td`
  padding-left: 19px;
  text-align: right;
`;

type ShippingOptionsDisplayProps = {
  quote: LiteQuote;
};

const ShippingOptionsDisplay = ({ quote }: ShippingOptionsDisplayProps) => {
  const hasValidShipping = useSelector(getHasValidShipping);
  const shippingCost = useSelector(getShippingCost);
  const shippingType = useSelector(getShippingType);
  const shippingRate = useSelector(getShippingRate);
  const subtotalCost = useSelector(getSubtotalCost);
  const taxCost = useSelector((state) => getTaxCost(state, quote));
  const totalCost = useSelector((state) => getTotalCost(state, quote));

  return hasValidShipping ? (
    <StyledTable>
      <tr>
        <InfoCell>
          {shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT
            ? SHIPPING_TYPE_LABEL[shippingType][PAYMENT_METHOD.CREDIT_CARD]
            : SHIPPING_TYPE_LABEL[shippingType]}
        </InfoCell>
        <LabelCell>Subtotal</LabelCell>
        <ValueCell>{`${formatPrice(subtotalCost, 2)}`}</ValueCell>
      </tr>
      {shippingCost > 0 && (
        <tr>
          <InfoCell />
          <LabelCell>Shipping Cost</LabelCell>
          <ValueCell>{`${formatPrice(shippingCost, 2)}`}</ValueCell>
        </tr>
      )}
      <tr>
        <InfoCell>
          Method:{' '}
          {shippingType === null
            ? 'None'
            : shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT && shippingRate
            ? SHIPPING_METHOD_LABEL[shippingRate.method]
            : SHIPPING_TYPE_LABEL[shippingType]}
        </InfoCell>
        <LabelCell>Tax</LabelCell>
        <ValueCell>{`${formatPrice(taxCost, 2)}`}</ValueCell>
      </tr>
      <tr>
        <InfoCell>
          {shippingRate &&
            shippingRate.arrivesOn &&
            `Arrives On: ${toMoment(shippingRate.arrivesOn)
              .utc()
              .format('dddd, MMM YY')}`}
        </InfoCell>
        <LabelCell>Order Total</LabelCell>
        <ValueCell>{`${formatPrice(totalCost, 2)}`}</ValueCell>
      </tr>
    </StyledTable>
  ) : null;
};

export default ShippingOptionsDisplay;
